import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, TextField, Box, Typography, createFilterOptions } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LinkIcon from '@mui/icons-material/Link';
import { BRAND, FI_ORANGE } from '../theme/tokens';

/**
 * LineCodeSelect — unified inline "search-or-create" code picker (v1.19).
 *
 * One control, two uses, identical feel:
 *   • Internal Code   — searches the inventory catalogue; "+ New" creates a Product.
 *   • Supplier Code   — searches the supplier price book; "+ New" creates a code.
 *
 * Behaviour mirrors the FieldInsight inventory dropdown: a pinned "+ New …" row,
 * then a searchable list of "CODE — description · meta". Free text is allowed
 * (so a supplier code we don't have yet can simply be typed). When empty and
 * required (internal code), it shows the red "needs a code" cue that drives the
 * unallocated-lines banner; otherwise a quiet "+ add" affordance.
 */

export interface LineCodeOption {
  key: string;
  code: string;
  description?: string;
  meta?: string;               // trailing, e.g. "$42.50"
  linkedCode?: string | null;  // supplier entry → linked internal code (if any)
}

interface CreateRow { __create: true; input: string }
interface AutoCreateRow { __autoCreate: true }
type Opt = LineCodeOption | CreateRow | AutoCreateRow;
const isCreate = (o: unknown): o is CreateRow =>
  typeof o === 'object' && o !== null && (o as CreateRow).__create === true;
const isAutoCreate = (o: unknown): o is AutoCreateRow =>
  typeof o === 'object' && o !== null && (o as AutoCreateRow).__autoCreate === true;

export interface LineCodeSelectProps {
  value: string;
  options: LineCodeOption[];
  onPick: (opt: LineCodeOption) => void;
  onCreateNew: (seed: string) => void;
  /** One-shot: auto-create internal inventory code from an existing supplier code. */
  onAutoCreate?: () => void;
  autoCreateLabel?: string;
  /** Preview of the next minted code — shown on the collapsed Auto-set button. */
  autoCreatePreview?: string;
  onFreeText?: (text: string) => void;
  newLabel: string;             // "New inventory item" / "New supplier code"
  placeholder?: string;
  accent?: string;              // code text colour
  uppercase?: boolean;
  /** Empty state required → red "needs a code" pill (internal code). */
  needsAction?: boolean;
  actionLabel?: string;         // "Select code"
  /** Empty state, not required → quiet add affordance (supplier code). */
  emptyLabel?: string;          // "Add supplier code"
}

const baseFilter = createFilterOptions<LineCodeOption>({
  stringify: o => `${o.code} ${o.description ?? ''}`,
  limit: 40,
});

export const LineCodeSelect: React.FC<LineCodeSelectProps> = ({
  value, options, onPick, onCreateNew, onAutoCreate, autoCreateLabel = 'Auto-set inventory code →',
  autoCreatePreview,
  onFreeText, newLabel, placeholder = 'Search…', accent = '#333', uppercase,
  needsAction, actionLabel = 'Select code', emptyLabel = 'Add code',
}) => {
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState(value);
  const acted = useRef(false);
  useEffect(() => { setInput(value); }, [value]);

  const startEdit = () => { setInput(value); acted.current = false; setEditing(true); };
  const stopEdit = () => setEditing(false);

  // ── Display (collapsed) ────────────────────────────────────────────────────
  if (!editing) {
    if (value) {
      return (
        <Box
          onClick={startEdit}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, cursor: 'pointer',
            '&:hover .lcs-caret': { opacity: 1 } }}
        >
          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 700, color: accent }}>
            {value}
          </Typography>
          <ArrowDropDownIcon className="lcs-caret" sx={{ fontSize: 15, color: '#bbb', opacity: 0.35, transition: 'opacity .15s' }} />
        </Box>
      );
    }
    if (needsAction) {
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }} className="no-print">
          <Box
            onClick={startEdit}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, cursor: 'pointer',
              backgroundColor: '#c62828', color: '#fff', borderRadius: 1, px: 0.9, py: 0.3,
              fontSize: '0.66rem', fontWeight: 700, whiteSpace: 'nowrap',
              '&:hover': { backgroundColor: '#b71c1c' } }}
          >
            <AddIcon sx={{ fontSize: 13 }} /> {actionLabel}
          </Box>
          {onAutoCreate && (
            <Box
              onClick={e => { e.stopPropagation(); onAutoCreate(); }}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35, cursor: 'pointer',
                backgroundColor: '#fff7ed', color: FI_ORANGE, border: `1px solid ${FI_ORANGE}55`,
                borderRadius: 1, px: 0.85, py: 0.28, fontSize: '0.64rem', fontWeight: 800,
                whiteSpace: 'nowrap',
                '&:hover': { backgroundColor: '#ffedd5', borderColor: FI_ORANGE } }}
              title={autoCreateLabel}
            >
              <AutoFixHighIcon sx={{ fontSize: 13 }} />
              Auto-set{autoCreatePreview ? ` ${autoCreatePreview}` : ''}
            </Box>
          )}
        </Box>
      );
    }
    return (
      <Box
        onClick={startEdit} className="no-print"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, cursor: 'pointer',
          color: '#bbb', fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap',
          '&:hover': { color: BRAND } }}
      >
        <AddIcon sx={{ fontSize: 13 }} /> {emptyLabel}
      </Box>
    );
  }

  // ── Editing (expanded combobox) ────────────────────────────────────────────
  return (
    <Autocomplete<Opt, false, false, true>
      open
      freeSolo
      size="small"
      options={options as Opt[]}
      inputValue={input}
      autoHighlight
      handleHomeEndKeys
      filterOptions={(opts, state) => {
        const filtered = baseFilter(opts as LineCodeOption[], state);
        const pinned: Opt[] = [];
        if (onAutoCreate) pinned.push({ __autoCreate: true });
        pinned.push({ __create: true, input: state.inputValue });
        return [...pinned, ...filtered];
      }}
      getOptionLabel={o => {
        if (typeof o === 'string') return o;
        if (isAutoCreate(o)) return autoCreateLabel;
        if (isCreate(o)) return o.input;
        return o.code;
      }}
      onInputChange={(_e, v, reason) => { if (reason === 'input') setInput(uppercase ? v.toUpperCase() : v); }}
      onChange={(_e, val) => {
        if (val == null) return;
        acted.current = true;
        if (typeof val === 'string') { onFreeText?.(val.trim()); stopEdit(); return; }
        if (isAutoCreate(val)) { onAutoCreate?.(); stopEdit(); return; }
        if (isCreate(val)) { onCreateNew(input.trim()); stopEdit(); return; }
        onPick(val); stopEdit();
      }}
      onClose={() => {
        // Commit free text typed but not explicitly chosen, then collapse.
        if (!acted.current) {
          const t = (uppercase ? input.trim().toUpperCase() : input.trim());
          if (t && t !== value) onFreeText?.(t);
        }
        stopEdit();
      }}
      renderOption={(props, o) => {
        if (isAutoCreate(o)) {
          return (
            <Box component="li" {...props} key="__autoCreate"
              sx={{ display: 'flex !important', alignItems: 'center', gap: 0.6,
                color: FI_ORANGE, fontWeight: 700, fontSize: '0.78rem', py: 0.7 }}>
              <AutoFixHighIcon sx={{ fontSize: 16 }} /> {autoCreateLabel}
            </Box>
          );
        }
        if (isCreate(o)) {
          return (
            <Box component="li" {...props} key="__create"
              sx={{ display: 'flex !important', alignItems: 'center', gap: 0.6,
                color: BRAND, fontWeight: 700, fontSize: '0.78rem', py: 0.7 }}>
              <AddIcon sx={{ fontSize: 16 }} /> New {newLabel}{o.input ? ` “${o.input}”` : ''}
            </Box>
          );
        }
        return (
          <Box component="li" {...props} key={o.key} sx={{ display: 'block !important', py: 0.55 }}>
            <Typography sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 700, color: accent, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {o.code}
              {o.linkedCode != null && (
                o.linkedCode
                  ? <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.66rem', color: FI_ORANGE, display: 'inline-flex', alignItems: 'center' }}>
                      <LinkIcon sx={{ fontSize: 12, mr: 0.2 }} />{o.linkedCode}
                    </Typography>
                  : <Typography component="span" sx={{ fontSize: '0.6rem', color: '#c62828', fontWeight: 700, border: '1px solid #ffcdd2', borderRadius: 0.5, px: 0.4 }}>
                      no inventory code
                    </Typography>
              )}
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: '#777' }} noWrap>
              {o.description}{o.meta ? ` · ${o.meta}` : ''}
            </Typography>
          </Box>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          variant="standard"
          autoFocus
          placeholder={placeholder}
          onKeyDown={e => { if (e.key === 'Escape') { acted.current = true; stopEdit(); } }}
          InputProps={{ ...params.InputProps, style: { fontFamily: 'monospace', fontSize: '0.75rem' } }}
        />
      )}
      sx={{ minWidth: 150 }}
    />
  );
};

export default LineCodeSelect;
